package com.videoanalyzer.web.screens

import androidx.compose.runtime.Composable
import com.videoanalyzer.web.components.InlineLoading
import com.videoanalyzer.web.models.UserProfile
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Settings/Account screen with profile info, subscription management,
 * and data privacy controls (GDPR compliance).
 */
@Composable
fun SettingsScreen(
    user: UserProfile,
    onManageSubscription: () -> Unit,
    onRefreshSubscription: () -> Unit,
    onDeleteAccount: () -> Unit,
    onExportData: () -> Unit,
    isLoading: Boolean,
    isSyncing: Boolean = false,
    isDeletingAccount: Boolean = false,
    isExportingData: Boolean = false,
    showDeleteConfirm: Boolean = false,
    onShowDeleteConfirm: (Boolean) -> Unit = {}
) {
    Div({
        style {
            maxWidth(600.px)
            margin(0.px, "auto".unsafeCast<CSSNumeric>())
        }
    }) {
        H1({
            style {
                fontSize(24.px)
                fontWeight(700)
                color(Color("#f8fafc"))
                marginBottom(24.px)
            }
        }) { Text("Account Settings") }

        // Profile card
        ProfileCard(user)

        // Subscription card
        SubscriptionCard(
            user = user,
            onManageSubscription = onManageSubscription,
            onRefreshSubscription = onRefreshSubscription,
            isLoading = isLoading,
            isSyncing = isSyncing
        )

        // Data Privacy card (GDPR)
        DataPrivacyCard(
            onExportData = onExportData,
            onDeleteAccount = onDeleteAccount,
            isExportingData = isExportingData,
            isDeletingAccount = isDeletingAccount,
            showDeleteConfirm = showDeleteConfirm,
            onShowDeleteConfirm = onShowDeleteConfirm
        )
    }
}

@Composable
private fun ProfileCard(user: UserProfile) {
    Div({
        classes("card")
        style { marginBottom(24.px) }
    }) {
        H2({
            style {
                fontSize(18.px)
                fontWeight(600)
                color(Color("#f8fafc"))
                marginBottom(16.px)
            }
        }) { Text("Profile") }

        SettingsRow("Email", user.email)
        SettingsRow("User ID", user.id)
        SettingsRow("Member since", user.createdAt?.substringBefore("T") ?: "N/A")
    }
}

@Composable
private fun SubscriptionCard(
    user: UserProfile,
    onManageSubscription: () -> Unit,
    onRefreshSubscription: () -> Unit,
    isLoading: Boolean,
    isSyncing: Boolean
) {
    Div({
        classes("card")
    }) {
        // Header with refresh button
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                marginBottom(16.px)
            }
        }) {
            H2({
                style {
                    fontSize(18.px)
                    fontWeight(600)
                    color(Color("#f8fafc"))
                    margin(0.px)
                }
            }) { Text("Subscription") }

            // Refresh button
            Button({
                style {
                    padding(6.px, 12.px)
                    backgroundColor(Color.transparent)
                    color(Color("#94a3b8"))
                    border(1.px, LineStyle.Solid, Color("rgba(255, 255, 255, 0.1)"))
                    borderRadius(6.px)
                    fontSize(12.px)
                    cursor(if (isSyncing) "wait" else "pointer")
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(6.px)
                    opacity(if (isSyncing) 0.6 else 1.0)
                }
                onClick { if (!isSyncing) onRefreshSubscription() }
                if (isSyncing) disabled()
            }) {
                if (isSyncing) {
                    Span({ style { fontSize(12.px) } }) { Text("↻") }
                    Text("Syncing...")
                } else {
                    Span({ style { fontSize(12.px) } }) { Text("↻") }
                    Text("Refresh")
                }
            }
        }

        SettingsRow("Plan", if (user.cancelAtPeriodEnd) "PRO (Cancelled)" else user.subscriptionTier.uppercase())
        SettingsRow("Analyses this month", "${user.analysesThisMonth}")
        if (user.creditBalance > 0) {
            SettingsRow("Credit balance", "${user.creditBalance} credits")
        }

        // Show cancellation notice if subscription is set to cancel
        if (user.cancelAtPeriodEnd && user.subscriptionEndDate != null) {
            CancellationNotice(
                endDate = user.subscriptionEndDate,
                onReactivate = onManageSubscription,
                isLoading = isLoading
            )
        }

        if (user.subscriptionTier == "pro" && !user.cancelAtPeriodEnd) {
            SubscriptionActions(
                onManageSubscription = onManageSubscription,
                isLoading = isLoading
            )
        }
    }
}

@Composable
private fun CancellationNotice(
    endDate: String,
    onReactivate: () -> Unit,
    isLoading: Boolean
) {
    Div({
        style {
            marginTop(16.px)
            padding(16.px)
            backgroundColor(Color("rgba(251, 191, 36, 0.1)"))
            border(1.px, LineStyle.Solid, Color("rgba(251, 191, 36, 0.3)"))
            borderRadius(8.px)
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(8.px)
                marginBottom(8.px)
            }
        }) {
            Span({ style { fontSize(18.px) } }) { Text("⚠️") }
            Span({
                style {
                    color(Color("#fbbf24"))
                    fontWeight(600)
                    fontSize(14.px)
                }
            }) { Text("Subscription Cancelled") }
        }
        P({
            style {
                color(Color("#94a3b8"))
                fontSize(13.px)
                margin(0.px)
                lineHeight("1.5")
            }
        }) {
            Text("Your Pro access will end on ")
            Span({
                style {
                    color(Color("#f8fafc"))
                    fontWeight(600)
                }
            }) { Text(endDate) }
            Text(". You can reactivate anytime before this date.")
        }

        Button({
            classes("btn")
            style {
                marginTop(12.px)
                padding(10.px, 20.px)
                property("background", "linear-gradient(135deg, #E53935 0%, #C62828 100%)")
                color(Color.white)
                borderRadius(8.px)
                fontSize(14.px)
                fontWeight(600)
                cursor("pointer")
            }
            onClick { onReactivate() }
            if (isLoading) disabled()
        }) {
            Text("Reactivate Subscription")
        }
    }
}

@Composable
private fun SubscriptionActions(
    onManageSubscription: () -> Unit,
    isLoading: Boolean
) {
    Div({
        style {
            marginTop(16.px)
            display(DisplayStyle.Flex)
            gap(12.px)
            flexWrap(FlexWrap.Wrap)
        }
    }) {
        Button({
            classes("btn", "btn-secondary")
            onClick { onManageSubscription() }
            if (isLoading) disabled()
        }) {
            if (isLoading) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(8.px)
                    }
                }) {
                    InlineLoading()
                    Text("Loading...")
                }
            } else {
                Text("Manage Subscription")
            }
        }

        Button({
            style {
                padding(10.px, 20.px)
                backgroundColor(Color.transparent)
                color(Color("#ef4444"))
                border(1.px, LineStyle.Solid, Color("#ef4444"))
                borderRadius(8.px)
                fontSize(14.px)
                cursor("pointer")
                property("transition", "all 0.2s ease")
            }
            onClick { onManageSubscription() }
            if (isLoading) disabled()
        }) {
            Text("Cancel Subscription")
        }
    }

    P({
        style {
            marginTop(12.px)
            fontSize(12.px)
            color(Color("#64748b"))
        }
    }) {
        Text("You can cancel anytime. Access continues until the end of your billing period.")
    }
}

@Composable
private fun DataPrivacyCard(
    onExportData: () -> Unit,
    onDeleteAccount: () -> Unit,
    isExportingData: Boolean,
    isDeletingAccount: Boolean,
    showDeleteConfirm: Boolean,
    onShowDeleteConfirm: (Boolean) -> Unit
) {
    Div({
        classes("card")
        style { marginTop(24.px) }
    }) {
        H2({
            style {
                fontSize(18.px)
                fontWeight(600)
                color(Color("#f8fafc"))
                marginBottom(16.px)
            }
        }) { Text("Data & Privacy") }

        P({
            style {
                fontSize(13.px)
                color(Color("#94a3b8"))
                marginBottom(16.px)
                lineHeight("1.5")
            }
        }) {
            Text("You have the right to access, export, and delete your personal data at any time.")
        }

        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(12.px)
            }
        }) {
            // Export Data button
            Button({
                style {
                    padding(12.px, 20.px)
                    backgroundColor(Color("rgba(255, 255, 255, 0.05)"))
                    color(Color("#f8fafc"))
                    border(1.px, LineStyle.Solid, Color("rgba(255, 255, 255, 0.1)"))
                    borderRadius(8.px)
                    fontSize(14.px)
                    cursor(if (isExportingData) "wait" else "pointer")
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(8.px)
                    width(100.percent)
                    justifyContent(JustifyContent.Center)
                }
                onClick { if (!isExportingData) onExportData() }
                if (isExportingData) disabled()
            }) {
                if (isExportingData) {
                    InlineLoading()
                    Text("Exporting...")
                } else {
                    Span({ style { fontSize(16.px) } }) { Text("\uD83D\uDCE5") }
                    Text("Export My Data")
                }
            }

            // Delete Account button or confirmation
            if (!showDeleteConfirm) {
                Button({
                    style {
                        padding(12.px, 20.px)
                        backgroundColor(Color.transparent)
                        color(Color("#ef4444"))
                        border(1.px, LineStyle.Solid, Color("#ef4444"))
                        borderRadius(8.px)
                        fontSize(14.px)
                        cursor("pointer")
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(8.px)
                        width(100.percent)
                        justifyContent(JustifyContent.Center)
                    }
                    onClick { onShowDeleteConfirm(true) }
                }) {
                    Span({ style { fontSize(16.px) } }) { Text("\uD83D\uDDD1\uFE0F") }
                    Text("Delete My Account")
                }
            } else {
                DeleteConfirmationDialog(
                    onCancel = { onShowDeleteConfirm(false) },
                    onConfirm = onDeleteAccount,
                    isDeleting = isDeletingAccount
                )
            }
        }
    }
}

@Composable
private fun DeleteConfirmationDialog(
    onCancel: () -> Unit,
    onConfirm: () -> Unit,
    isDeleting: Boolean
) {
    Div({
        style {
            padding(16.px)
            backgroundColor(Color("rgba(239, 68, 68, 0.1)"))
            border(1.px, LineStyle.Solid, Color("rgba(239, 68, 68, 0.3)"))
            borderRadius(8.px)
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(8.px)
                marginBottom(12.px)
            }
        }) {
            Span({ style { fontSize(20.px) } }) { Text("⚠️") }
            Span({
                style {
                    color(Color("#ef4444"))
                    fontWeight(600)
                    fontSize(14.px)
                }
            }) { Text("Are you sure?") }
        }

        P({
            style {
                fontSize(13.px)
                color(Color("#94a3b8"))
                marginBottom(16.px)
                lineHeight("1.5")
            }
        }) {
            Text("This will permanently delete your account and all your data including analysis history. This action cannot be undone.")
        }

        Div({
            style {
                display(DisplayStyle.Flex)
                gap(12.px)
            }
        }) {
            Button({
                style {
                    flex(1)
                    padding(10.px, 16.px)
                    backgroundColor(Color("rgba(255, 255, 255, 0.05)"))
                    color(Color("#f8fafc"))
                    border(1.px, LineStyle.Solid, Color("rgba(255, 255, 255, 0.1)"))
                    borderRadius(8.px)
                    fontSize(14.px)
                    cursor("pointer")
                }
                onClick { onCancel() }
            }) {
                Text("Cancel")
            }

            Button({
                style {
                    flex(1)
                    padding(10.px, 16.px)
                    backgroundColor(Color("#ef4444"))
                    color(Color.white)
                    border(0.px, LineStyle.None, Color.transparent)
                    borderRadius(8.px)
                    fontSize(14.px)
                    fontWeight(600)
                    cursor(if (isDeleting) "wait" else "pointer")
                }
                onClick { if (!isDeleting) onConfirm() }
                if (isDeleting) disabled()
            }) {
                if (isDeleting) {
                    InlineLoading()
                    Text("Deleting...")
                } else {
                    Text("Delete Forever")
                }
            }
        }
    }
}

@Composable
private fun SettingsRow(label: String, value: String) {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            paddingTop(8.px)
            paddingBottom(8.px)
            property("border-bottom", "1px solid #334155")
        }
    }) {
        Span({
            style { color(Color("#94a3b8")) }
        }) { Text(label) }
        Span({
            style { color(Color("#f8fafc")) }
        }) { Text(value) }
    }
}
